import { useEffect, useRef } from 'react';
import { TextField, InputAdornment, Tooltip, IconButton, Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getBankNameFromIban, normalizeIban } from '../helpers/iban';

const IBAN_HINT =
    'Podaj 28-znakowy numer rachunku bankowego z prefiksem PL (np. PL61 1090 1014 0000 0712 1981 2874).';

interface IbanFieldProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: boolean;
    helperText?: React.ReactNode;
    placeholder: string;
    /** Wartość pola "Nazwa banku" — trzymana w formiku, więc trafia do wysyłanych danych. */
    bankName: string;
    /** Ustawia wartość pola "Nazwa banku" (formik.setFieldValue). */
    setBankName: (value: string) => void;
    /** Ustawia wartość numeru konta — używane do usunięcia spacji po wyjściu z pola. */
    setValue: (value: string) => void;
    /**
     * Etykiety nad polami. Formularz osobowy ma opisy w lewej kolumnie wiersza,
     * więc ich nie powtarzamy; B2B nie ma takiej kolumny i bez nich pola byłyby nieopisane.
     */
    inlineLabels?: boolean;
    name?: string;
    bankFieldName?: string;
    sx?: object;
}

/**
 * Pole numeru konta wspólne dla formularza osobowego i B2B.
 *
 * Nazwa banku jest zwykłym polem tekstowym — podpowiadamy ją na podstawie numeru IBAN,
 * ale użytkownik może ją nadpisać (np. bank spółdzielczy albo nieaktualny wpis w naszej
 * liście kodów). Po ręcznej edycji przestajemy nadpisywać wpisaną wartość.
 */
const IbanField = ({
    value, onChange, onBlur, error, helperText, bankName, setBankName, setValue, placeholder,
    inlineLabels = false, name = 'accountNumber', bankFieldName = 'bankName', sx,
}: IbanFieldProps) => {
    // Pamiętamy ostatnią podpowiedź, żeby odróżnić "użytkownik nic nie zmieniał"
    // od "użytkownik świadomie wpisał własną nazwę".
    const lastSuggestion = useRef<string>('');

    useEffect(() => {
        const suggestion = getBankNameFromIban(value) ?? '';
        const untouched = bankName === '' || bankName === lastSuggestion.current;
        if (suggestion !== lastSuggestion.current && untouched) {
            setBankName(suggestion);
        }
        lastSuggestion.current = suggestion;
        // setBankName pochodzi z formika i zmienia tożsamość co render — pominięty celowo,
        // efekt ma reagować wyłącznie na zmianę numeru konta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const label = (text: string) => (
        <Typography sx={{ fontSize: '14px', textAlign: 'left', marginBottom: '4px' }}>
            {text}
        </Typography>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {inlineLabels && label('Numer konta (IBAN)')}
            <TextField
                fullWidth
                sx={sx}
                name={name}
                value={value}
                onChange={onChange}
                // Wpisywanie ze spacjami jest wygodniejsze, ale do backendu ma trafić
                // numer ciągiem — normalizujemy po wyjściu z pola.
                onBlur={(e) => {
                    const normalized = normalizeIban(value);
                    if (normalized !== value) setValue(normalized);
                    onBlur(e as React.FocusEvent<HTMLInputElement>);
                }}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={IBAN_HINT} enterTouchDelay={0} leaveTouchDelay={5000}>
                                {/* tabIndex=-1, żeby ikona nie łapała focusu przy przechodzeniu tabem */}
                                <IconButton aria-label={IBAN_HINT} edge="end" size="small" tabIndex={-1}>
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />

            <Box sx={{ marginTop: '12px' }}>
                {inlineLabels && label('Nazwa banku')}
                <TextField
                    fullWidth
                    sx={sx}
                    name={bankFieldName}
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    inputProps={{ 'aria-label': 'Nazwa banku' }}
                />
            </Box>
        </Box>
    );
};

export default IbanField;
